import React, { useEffect, useState, useRef } from 'react';
import { TrendingUp, TrendingDown, Edit2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  description: string;
  percentage: string;
  trend: 'up' | 'down';
  isEditable?: boolean;
  onValueChange?: (value: number) => void;
}
export function MetricCard({
  title,
  value,
  subtitle,
  icon,
  description,
  percentage,
  trend,
  isEditable = false,
  onValueChange
}: MetricCardProps) {
  const {
    theme
  } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const numericValue = typeof value === 'number' ? value : parseFloat(value.replace(/[^0-9.-]/g, ''));
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEditable && onValueChange) {
      setEditValue(numericValue.toString());
      setIsEditing(true);
      // Call onValueChange immediately to trigger modal in summary view
      // or start inline editing in specific week view
      onValueChange(numericValue);
    }
  };
  const handleSave = () => {
    const newValue = parseFloat(editValue);
    if (!isNaN(newValue) && onValueChange) {
      onValueChange(newValue);
    }
    setIsEditing(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };
  return <div className={`rounded-xl border p-4 transition-all ${theme === 'dark' ? 'bg-[#1f1f1f] border-[#2a2a2a] hover:border-[#3a3a3a]' : 'bg-white border-gray-200 hover:border-gray-300'} ${isEditing ? theme === 'dark' ? 'ring-2 ring-[#FF7A59]/50 border-[#FF7A59]' : 'ring-2 ring-[#FF7A59]/50 border-[#FF7A59]' : ''} ${isEditable ? 'cursor-pointer' : ''}`} onClick={isEditable ? handleClick : undefined} data-id="element-27">
      <div className="flex items-start justify-between mb-4" data-id="element-28">
        <h3 className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-29">
          {title}
        </h3>
        <div className="flex items-center gap-2" data-id="element-30">
          {isEditable && !isEditing && <Edit2 className={`w-3 h-3 transition-colors ${theme === 'dark' ? 'text-[#FF9575]' : 'text-[#FF6B4A]'}`} data-id="element-31" />}
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${trend === 'up' ? 'bg-[#FF7A59]/10 text-[#FF7A59]' : 'bg-red-500/10 text-red-500'}`} data-id="element-32">
            {trend === 'up' ? <TrendingUp className="w-3 h-3" data-id="element-33" /> : <TrendingDown className="w-3 h-3" data-id="element-34" />}
            {percentage}%
          </div>
        </div>
      </div>
      <div className="mb-3" data-id="element-35">
        {isEditing ? <input ref={inputRef} type="number" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={handleSave} onKeyDown={handleKeyDown} onClick={e => e.stopPropagation()} className={`text-3xl font-bold w-full bg-transparent border-b-2 focus:outline-none ${theme === 'dark' ? 'text-white border-[#FF7A59]' : 'text-black border-[#FF7A59]'}`} data-id="element-36" /> : <p className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-black'} ${isEditable ? 'hover:text-[#FF7A59] transition-colors' : ''}`} data-id="element-37">
            {value}
          </p>}
      </div>
      <div className="space-y-0.5" data-id="element-38">
        <div className={`flex items-center gap-2 text-xs ${theme === 'dark' ? 'text-white' : 'text-black'}`} data-id="element-39">
          {icon}
          <span data-id="element-40">{subtitle}</span>
        </div>
        <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`} data-id="element-41">
          {description}
        </p>
      </div>
    </div>;
}